import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Tabs,
  Tab,
  TextField,
  Button,
  CircularProgress,
  Alert,
  Card,
  CardContent,
  Divider,
} from '@mui/material';
import {
  Gavel as LegalIcon,
  Save as SaveIcon,
  DirectionsCar as DriverIcon,
  Person as PassengerIcon,
} from '@mui/icons-material';
import apiService from '../services/apiService';

const LegalPagesPage = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Tab state: 0 = Terms & Conditions, 1 = Privacy Policy
  const [pageTypeTab, setPageTypeTab] = useState(0);
  // Sub-tab state: 0 = Driver, 1 = Passenger
  const [userTypeTab, setUserTypeTab] = useState(0);

  const [formData, setFormData] = useState({
    title: '',
    content: '',
    version: '1.0',
  });

  const pageTypes = ['terms_conditions', 'privacy_policy'];
  const userTypes = ['driver', 'passenger'];
  const pageTypeLabels = ['Terms & Conditions', 'Privacy Policy'];
  const userTypeLabels = ['Driver', 'Passenger'];

  useEffect(() => {
    fetchLegalPage();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pageTypeTab, userTypeTab]);

  const fetchLegalPage = async () => {
    try {
      setLoading(true);
      setError('');
      const pageType = pageTypes[pageTypeTab];
      const userType = userTypes[userTypeTab];

      const response = await apiService.getLegalPage(pageType, userType);
      if (response.success && response.data) {
        setFormData({
          title: response.data.title || '',
          content: response.data.content || '',
          version: response.data.version || '1.0',
        });
      } else {
        setFormData({
          title: `${pageTypeLabels[pageTypeTab]} for ${userTypeLabels[userTypeTab]}s`,
          content: '',
          version: '1.0',
        });
      }
    } catch (err) {
      console.error('Fetch legal page error:', err);
      setError('Failed to fetch legal page');
      setFormData({
        title: `${pageTypeLabels[pageTypeTab]} for ${userTypeLabels[userTypeTab]}s`,
        content: '',
        version: '1.0',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (!formData.content.trim()) {
      setError('Content is required');
      return;
    }

    try {
      setSaving(true);
      setError('');
      const pageType = pageTypes[pageTypeTab];
      const userType = userTypes[userTypeTab];

      await apiService.updateLegalPage(pageType, userType, formData);
      setSuccess(`${pageTypeLabels[pageTypeTab]} for ${userTypeLabels[userTypeTab]}s saved successfully`);
    } catch (err) {
      console.error('Save legal page error:', err);
      setError(err.message || 'Failed to save legal page');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
        <LegalIcon sx={{ fontSize: 32, color: '#FFC107' }} />
        <Typography variant="h4" fontWeight="bold">
          Legal Pages
        </Typography>
      </Box>

      {/* Alerts */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>
          {success}
        </Alert>
      )}

      {/* Page Type Tabs */}
      <Paper sx={{ mb: 2 }}>
        <Tabs
          value={pageTypeTab}
          onChange={(e, newValue) => setPageTypeTab(newValue)}
          sx={{
            '& .MuiTab-root.Mui-selected': { color: '#FFC107' },
            '& .MuiTabs-indicator': { bgcolor: '#FFC107' },
          }}
        >
          <Tab icon={<LegalIcon />} label="Terms & Conditions" iconPosition="start" />
          <Tab icon={<LegalIcon />} label="Privacy Policy" iconPosition="start" />
        </Tabs>
      </Paper>

      {/* User Type Tabs */}
      <Paper sx={{ mb: 3 }}>
        <Tabs
          value={userTypeTab}
          onChange={(e, newValue) => setUserTypeTab(newValue)}
          sx={{
            '& .MuiTab-root.Mui-selected': { color: '#FFC107' },
            '& .MuiTabs-indicator': { bgcolor: '#FFC107' },
          }}
        >
          <Tab icon={<DriverIcon />} label="For Drivers" iconPosition="start" />
          <Tab icon={<PassengerIcon />} label="For Passengers" iconPosition="start" />
        </Tabs>
      </Paper>

      {/* Content Editor */}
      <Card>
        <CardContent>
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
              <CircularProgress sx={{ color: '#FFC107' }} />
            </Box>
          ) : (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
              <Typography variant="h6" color="textSecondary">
                {pageTypeLabels[pageTypeTab]} - {userTypeLabels[userTypeTab]}s
              </Typography>

              <Divider />

              <TextField
                label="Title"
                fullWidth
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                placeholder="Enter page title"
              />

              <TextField
                label="Version"
                value={formData.version}
                onChange={(e) => setFormData({ ...formData, version: e.target.value })}
                placeholder="1.0"
                sx={{ width: 150 }}
              />

              <TextField
                label="Content"
                fullWidth
                multiline
                rows={20}
                value={formData.content}
                onChange={(e) => setFormData({ ...formData, content: e.target.value })}
                placeholder={`Enter ${pageTypeLabels[pageTypeTab].toLowerCase()} content here...\n\nYou can use plain text or HTML formatting.`}
                helperText="Enter the full content. HTML tags are supported for formatting."
              />

              <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
                <Button
                  variant="contained"
                  startIcon={saving ? <CircularProgress size={20} color="inherit" /> : <SaveIcon />}
                  onClick={handleSave}
                  disabled={saving}
                  sx={{
                    bgcolor: '#FFC107',
                    color: '#000',
                    '&:hover': { bgcolor: '#FFD54F' },
                  }}
                >
                  {saving ? 'Saving...' : 'Save Changes'}
                </Button>
              </Box>
            </Box>
          )}
        </CardContent>
      </Card>
    </Box>
  );
};

export default LegalPagesPage;
